/**
 * sdk-custom-events — data layer (feature FS-206).
 *
 * Thin typed wrapper over `apiClient` for the per-tenant SDK event-type
 * registry:
 *   GET    /api/v1/sdk-custom-events?category=
 *   POST   /api/v1/sdk-custom-events
 *   PATCH  /api/v1/sdk-custom-events/:id
 *   DELETE /api/v1/sdk-custom-events/:id
 *   GET    /api/v1/sdk-custom-events/stats
 *
 * Pure helpers (validators, formatters, query keys) are exported so the page
 * component and the unit tests can share contracts with the server.
 */
import { apiClient } from "../../api-client";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export const EVENT_CATEGORIES = [
	"engagement",
	"monetization",
	"social",
	"game",
	"onboarding",
	"custom",
] as const;
export type EventCategory = (typeof EVENT_CATEGORIES)[number];

export interface SdkEventType {
	id: string;
	name: string;
	category: EventCategory;
	display_name: string;
	default_payout_cents: number;
	active: boolean;
	volume_lifetime: number;
	created_at: number;
	updated_at: number;
	deleted_at: number | null;
}

export interface ListEventTypesResponse {
	count: number;
	items: SdkEventType[];
}

export interface StatsResponse {
	total_events: number;
	by_category: Record<EventCategory, number>;
	top_10_by_volume: Array<{
		id: string;
		name: string;
		category: EventCategory;
		volume_lifetime: number;
	}>;
}

// ---------------------------------------------------------------------------
// Validators
// ---------------------------------------------------------------------------

export const NAME_RE = /^[a-z][a-z0-9_]{1,49}$/;
export const DISPLAY_NAME_MAX = 120;

export function normalizeName(raw: string): string {
	return String(raw ?? "").trim().toLowerCase();
}

export function validateName(raw: string): string | null {
	const normalized = normalizeName(raw);
	if (!NAME_RE.test(normalized)) return null;
	return normalized;
}

export function validateCategory(raw: string): EventCategory | null {
	return (EVENT_CATEGORIES as readonly string[]).includes(raw)
		? (raw as EventCategory)
		: null;
}

export function validateDisplayName(
	raw: string | null | undefined,
): string | null | "invalid" {
	if (raw == null) return null;
	const trimmed = String(raw).trim();
	if (!trimmed) return null;
	if (trimmed.length > DISPLAY_NAME_MAX) return "invalid";
	return trimmed;
}

export function validatePayoutCents(
	raw: string | null | undefined,
): number | null | "invalid" {
	if (raw == null || raw === "") return null;
	const trimmed = String(raw).trim();
	if (!trimmed) return null;
	const n = Number(trimmed);
	if (
		!Number.isFinite(n) ||
		!Number.isInteger(n) ||
		n < 0 ||
		n > 100_000_000_000
	) {
		return "invalid";
	}
	return n;
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const sdkCustomEventsQueryKeys = {
	all: ["sdk-custom-events"] as const,
	list: (category?: EventCategory | null) =>
		[...sdkCustomEventsQueryKeys.all, "list", category ?? "all"] as const,
	stats: () => [...sdkCustomEventsQueryKeys.all, "stats"] as const,
};

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

export function formatDate(unixSec: number): string {
	if (!Number.isFinite(unixSec) || unixSec <= 0) return "—";
	return new Date(unixSec * 1000).toLocaleString("en-US", {
		year: "numeric",
		month: "short",
		day: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	});
}

export function formatCents(cents: number | null | undefined): string {
	if (cents == null || !Number.isFinite(cents)) return "—";
	const dollars = cents / 100;
	try {
		return new Intl.NumberFormat("en-US", {
			style: "currency",
			currency: "USD",
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		}).format(dollars);
	} catch {
		return `$${dollars.toFixed(2)}`;
	}
}

export function formatVolume(n: number): string {
	if (!Number.isFinite(n) || n < 0) return "0";
	return n.toLocaleString("en-US");
}

export function categoryLabel(cat: EventCategory): string {
	switch (cat) {
		case "engagement":
			return "Engagement";
		case "monetization":
			return "Monetization";
		case "social":
			return "Social";
		case "game":
			return "Game";
		case "onboarding":
			return "Onboarding";
		case "custom":
			return "Custom";
	}
}

// ---------------------------------------------------------------------------
// Remote bindings
// ---------------------------------------------------------------------------

const BASE = "/sdk-custom-events";

export async function listEventTypes(
	category?: EventCategory | null,
): Promise<ListEventTypesResponse> {
	const query: Record<string, string> = {};
	if (category) query.category = category;
	return apiClient.get<ListEventTypesResponse>(BASE, { query });
}

export interface CreateEventTypeArgs {
	name: string;
	category?: EventCategory;
	display_name: string;
	default_payout_cents?: number;
}

export async function createEventType(
	args: CreateEventTypeArgs,
): Promise<SdkEventType> {
	const payload: Record<string, unknown> = {
		name: args.name,
		display_name: args.display_name,
	};
	if (args.category) payload.category = args.category;
	if (args.default_payout_cents !== undefined)
		payload.default_payout_cents = args.default_payout_cents;
	const res = await apiClient.post<{ event_type: SdkEventType }>(
		BASE,
		payload,
	);
	return res.event_type;
}

export interface UpdateEventTypeArgs {
	id: string;
	name?: string;
	category?: EventCategory;
	display_name?: string;
	default_payout_cents?: number;
}

export async function updateEventType(
	args: UpdateEventTypeArgs,
): Promise<SdkEventType> {
	const { id, ...rest } = args;
	const res = await apiClient.patch<{ event_type: SdkEventType }>(
		`${BASE}/${encodeURIComponent(id)}`,
		rest,
	);
	return res.event_type;
}

export async function deleteEventType(id: string): Promise<SdkEventType> {
	const res = await apiClient.delete<{ event_type: SdkEventType }>(
		`${BASE}/${encodeURIComponent(id)}`,
	);
	return res.event_type;
}

export async function fetchStats(): Promise<StatsResponse> {
	return apiClient.get<StatsResponse>(`${BASE}/stats`);
}
